import { useMemo } from 'react';
import { useLibkeyMigrationReviewerId } from '../LibkeyProvider';

/**
 * Per-browser "never prompt again" flags for the Nomad migration nudge.
 * Deliberately localStorage (not server-side): per-device suppression matches
 * the requirement, and a declined reviewer on another browser simply sees the
 * prompt once more there.
 *
 * Keys are scoped by reviewer id so two reviewers sharing a browser/device do
 * not inherit each other's dismissal — without the suffix, Reviewer A declining
 * would silently suppress Reviewer B's first-visit prompt.
 */
const DISMISSAL_KEY_BASE = 'covidence.libkey.nomadMigrationDismissed';
const BANNER_DISMISSAL_KEY_BASE =
  'covidence.libkey.nomadMigrationBannerDismissed';

export const dismissalKey = (reviewerId: number): string =>
  `${DISMISSAL_KEY_BASE}.${reviewerId}`;

export const bannerDismissalKey = (reviewerId: number): string =>
  `${BANNER_DISMISSAL_KEY_BASE}.${reviewerId}`;

// localStorage access can throw (private browsing, blocked storage); treat
// storage failures as "not dismissed" / silently skip the write.
const isDismissed = (reviewerId: number): boolean => {
  try {
    return window.localStorage.getItem(dismissalKey(reviewerId)) === 'true';
  } catch {
    return false;
  }
};

const markDismissed = (reviewerId: number): void => {
  try {
    window.localStorage.setItem(dismissalKey(reviewerId), 'true');
  } catch {
    // Nothing to do: the reviewer will be prompted again next session.
  }
};

const isBannerDismissed = (reviewerId: number): boolean => {
  try {
    return (
      window.localStorage.getItem(bannerDismissalKey(reviewerId)) === 'true'
    );
  } catch {
    return false;
  }
};

const markBannerDismissed = (reviewerId: number): void => {
  try {
    window.localStorage.setItem(bannerDismissalKey(reviewerId), 'true');
  } catch {
    // Nothing to do: the reviewer will be prompted again next session.
  }
};

export type NomadMigrationDismissal = {
  // The popup dismissal ("Maybe later"): steps the popup down to the banner.
  isDismissed: () => boolean;
  markDismissed: () => void;
  // The stronger banner dismissal: suppresses the nudge entirely on this device.
  isBannerDismissed: () => boolean;
  markBannerDismissed: () => void;
};

/**
 * The dismissal flags for the current reviewer, with the per-reviewer-id key
 * scoping handled internally so callers don't have to thread the id around.
 * Before the reviewer resolves the id is null: reads report "not dismissed" and
 * writes are no-ops, which is safe because the nudge only engages once the
 * reviewer (and therefore eligibility) has resolved.
 */
export const useNomadMigrationDismissal = (): NomadMigrationDismissal => {
  const reviewerId = useLibkeyMigrationReviewerId();
  return useMemo(
    () => ({
      isDismissed: () => reviewerId !== null && isDismissed(reviewerId),
      markDismissed: () => {
        if (reviewerId !== null) {
          markDismissed(reviewerId);
        }
      },
      isBannerDismissed: () =>
        reviewerId !== null && isBannerDismissed(reviewerId),
      markBannerDismissed: () => {
        if (reviewerId !== null) {
          markBannerDismissed(reviewerId);
        }
      },
    }),
    [reviewerId]
  );
};
